import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Scale, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import PublicFooter from "@/components/PublicFooter";

export default function PublicSubmit() {
  const { agencyId } = useParams();
  const [agency, setAgency] = useState(null);
  const [form, setForm] = useState({ requester_name: "", requester_email: "", subject: "", description: "", website: "" });
  const [submitted, setSubmitted] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/agencies/${agencyId}`).then((r) => setAgency(r.data)).catch(() => setErr("Agency not found"));
  }, [agencyId]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/public/requests", { ...form, agency_id: agencyId });
      setSubmitted(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center gap-3">
          <Scale className="w-5 h-5 text-[#0B1B3D]" strokeWidth={1.75} />
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-slate-500">Public Records Request</div>
            <div className="font-serif text-xl">{agency?.name || "—"}</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 lg:p-10">
        {submitted ? (
          <div className="fa-card p-10 text-center">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-mono text-xs uppercase tracking-widest text-emerald-700 mb-2">Request Received</div>
            <h2 className="font-serif text-3xl mb-3">Case #{submitted.case_number}</h2>
            <p className="text-slate-600 mb-2">{submitted.message}</p>
            <p className="text-sm text-slate-500">Statutory response deadline: {new Date(submitted.deadline).toLocaleDateString()}</p>
          </div>
        ) : (
          <form onSubmit={submit} className="fa-card p-8 space-y-5">
            {/* Honeypot */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="hidden"
              aria-hidden="true"
              data-testid="public-honeypot"
            />
            <h1 className="font-serif text-3xl tracking-tight font-medium">Submit a public records request</h1>
            <p className="text-sm text-slate-600">Your request will be assigned a case number and processed within the statutory response window.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="fa-label">Your name</label><input data-testid="public-name" required value={form.requester_name} onChange={(e) => setForm({ ...form, requester_name: e.target.value })} className="fa-input" /></div>
              <div><label className="fa-label">Your email</label><input data-testid="public-email" type="email" required value={form.requester_email} onChange={(e) => setForm({ ...form, requester_email: e.target.value })} className="fa-input" /></div>
            </div>
            <div><label className="fa-label">Subject</label><input data-testid="public-subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="fa-input" /></div>
            <div><label className="fa-label">Description of records sought</label><textarea data-testid="public-description" required rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="fa-input" /></div>
            {err && <div className="text-red-700 bg-red-50 border border-red-200 p-3 text-sm">{String(err)}</div>}
            <button data-testid="public-submit" disabled={busy} className="fa-btn-primary">{busy ? "Submitting…" : "Submit request"}</button>
            <p className="text-xs text-slate-500">Powered by <Link to="/" className="underline">FOIA Assist</Link></p>
          </form>
        )}
      </main>
      <PublicFooter />
    </div>
  );
}
